import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useNotify } from '@/api/notify';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice } from '@/components/PageHeading';

import { platformKeys, setBillingTerms, usePlans, useTenantBilling } from './api';
import { todayIso, termsDraftFrom, termsProblems, termsRequest, type TermsDraft } from './billing';
import { TermsFields } from './TermsFields';

/**
 * A company's terms (docs/Platform_Billing_Design.md §4.2–§4.3), as a page. Saving starts a new
 * subscription row and carries the days already paid for across, so it never gives or takes any.
 */
export function TermsPage({ tenantId, onBack }: { tenantId: number; onBack: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const billing = useTenantBilling(tenantId);
  const plans = usePlans();
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const [draft, setDraft] = useState<TermsDraft | null>(null);
  const [busy, setBusy] = useState(false);

  // Once, when the billing arrives — a refetch must not overwrite what is being typed.
  useEffect(() => {
    if (billing.data && !draft) setDraft(termsDraftFrom(billing.data));
  }, [billing.data, draft]);

  if (!draft || !billing.data) return null;

  const plan = plans.data?.find((p) => p.code === draft.planCode);
  const problems = termsProblems(draft, plan, todayIso());

  const save = async () => {
    setBusy(true);
    try {
      await setBillingTerms(tenantId, termsRequest(draft));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.billing(tenantId) }),
        queryClient.invalidateQueries({ queryKey: platformKeys.companies }),
      ]);
      notify.success('Terms saved.');
      onBack();
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to the company"
          className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
        >
          <i className="icon icon-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">{billing.data.terms ? 'Change terms' : 'Set terms'}</h1>
          <p className="page-subtitle">
            {billing.data.tenantName} · days already paid for carry over to the new terms.
          </p>
        </div>
      </div>

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <TermsFields
            draft={draft}
            onChange={(update) => setDraft((d) => (d ? update(d) : d))}
            plans={plans.data ?? []}
          />
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          {problems.length > 0 && (
            <Notice tone="warning" title="Before saving">
              <ul className="ps-0">
                {problems.map((p) => (
                  <li key={p} className="list-none">
                    {p}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          {!billing.data.terms &&
            billing.data.payments.length === 0 &&
            draft.chargingModel !== 'Pilot' && (
              <Notice title="Due from today">
                With nothing paid yet, a company on these terms owes from the day they start. Record
                its first payment next, or it goes read-only when its grace runs out.
              </Notice>
            )}
          <Card>
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                loading={busy}
                disabled={busy || problems.length > 0}
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : 'Save terms'}
              </Button>
              <Button disabled={busy} shortcut="back" onClick={onBack}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
